/**
 * What the block dialogs share (FLOW-376 acts, moved into modals; FLOW-401's
 * pop-ups). Each dialog runs its write itself and closes only once it has
 * landed, so a refused write leaves the form open with what was typed (the
 * Tier 2 §4b Ops action-dialog standard, as `ConfirmDialogComponent`).
 */
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog';

export const CONTEXT_WRITE_REFUSED = 'That could not be saved. Please try again.';

/** The server's refusal in its own words, else a plain retry line. */
export function contextWriteError(err: unknown): string {
  const detail = (err as { error?: { detail?: unknown } })?.error?.detail;
  return typeof detail === 'string' ? detail : CONTEXT_WRITE_REFUSED;
}

/**
 * One size for every pop-up (DOC-89-A §3): as wide as 36rem, never wider than
 * the screen less a gutter — so nothing scrolls sideways down to a phone.
 */
export const CONTEXT_DIALOG: MatDialogConfig = {
  width: '36rem',
  maxWidth: 'calc(100vw - 2rem)',
  autoFocus: 'first-tabbable',
};

/**
 * DOC-89-A §3: a pop-up closes on Close, Escape or a press outside it, and
 * **nothing typed is lost without a question**. Takes the dialog's own close
 * routes over (`disableClose`), and returns the one `close` the Cancel / Close
 * button should call: it closes at once while nothing is typed, else asks
 * *Discard what you typed?* first.
 */
export function guardTypedInput(
  ref: MatDialogRef<unknown>,
  dialog: MatDialog,
  isDirty: () => boolean,
): () => void {
  ref.disableClose = true;
  const attempt = (): void => {
    if (!isDirty()) {
      ref.close(false);
      return;
    }
    const data: ConfirmDialogData = {
      title: 'Discard what you typed?',
      message: 'Nothing typed here has been saved yet.',
      confirmLabel: 'Discard',
      cancelLabel: 'Keep typing',
      destructive: true,
    };
    dialog
      .open(ConfirmDialogComponent, { data, width: '400px' })
      .afterClosed()
      .subscribe((discard) => {
        if (discard) ref.close(false);
      });
  };
  ref.backdropClick().subscribe(attempt);
  ref.keydownEvents().subscribe((event) => {
    if (event.key === 'Escape') attempt();
  });
  return attempt;
}
